import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  LinearProgress,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import FolderIcon from "@mui/icons-material/Folder";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import StorageIcon from "@mui/icons-material/Storage";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";
import { Face } from "@mui/icons-material";

// ─── Unified StatCard — handles both normal and storage variant ───
const StatCard = ({ title, value, icon: Icon, color, subtitle, progress }) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${alpha(color, 0.18)}`,
        borderRadius: 3,
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 12px 32px ${alpha(color, 0.22)}`,
        },
      }}
    >
      <CardContent
        sx={{
          p: 2.5,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          "&:last-child": { pb: 2.5 },
        }}
      >
        {/* Top row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                fontSize: "0.68rem",
                color: "text.secondary",
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mt: 0.5,
                letterSpacing: "-0.03em",
                color: color,
                lineHeight: 1.1,
              }}
            >
              {value}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: 0.4, display: "block" }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.7)})`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 6px 16px ${alpha(color, 0.35)}`,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 24, color: "#fff" }} />
          </Box>
        </Stack>

        {/* Bottom — progress bar only for storage variant */}
        {progress !== undefined && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={progress.percent}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(color, 0.12),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.6)})`,
                },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.6, display: "block" }}
            >
              {progress.percent.toFixed(1)}% of {progress.max} GB used
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const UserDashboardHome = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const [profile, setProfile] = useState(null);
  const [downloads, setDownloads] = useState([]);

  useEffect(() => {
    loadProfile();
    loadDownloads();
  }, []);

  const loadProfile = async () => {
    const res = await hitAxios({
      path: "/api/user/profile",
      showSnackbar: false,
    });
    if (res?.data?.success) setProfile(res.data.data);
  };

  const loadDownloads = async () => {
    const res = await hitAxios({
      path: "/api/torrent/my-downloads?limit=5",
      showSnackbar: false,
    });
    if (res?.data?.success) setDownloads(res.data.data);
  };

  const storageUsedGB = profile
    ? (profile.storage_used / 1024 ** 3).toFixed(2)
    : 0;

  const storagePercent = profile?.max_storage_gb
    ? Math.min((storageUsedGB / profile.max_storage_gb) * 100, 100)
    : 0;

  const statusColor = (s) =>
    ({
      completed: "success",
      downloading: "info",
      pending: "warning",
      failed: "error",
    })[s] || "default";

  const cards = [
    {
      title: lang.currentPlan || "Current Plan",
      value: profile?.plan_name || "Free",
      icon: CardMembershipIcon,
      color: theme.palette.primary.main,
      subtitle: profile?.plan_expires_at
        ? `Expires ${new Date(profile.plan_expires_at).toLocaleDateString()}`
        : "No expiry",
    },
    {
      title: lang.totalDownloads || "Total Downloads",
      value: downloads.length,
      icon: CloudDownloadIcon,
      color: theme.palette.success.main,
    },
    {
      title: lang.maxDownloads || "Max Downloads",
      value: profile?.max_downloads || 0,
      icon: FolderIcon,
      color: theme.palette.warning.main,
    },
    {
      title: lang.storageUsed || "Storage Used",
      value: `${storageUsedGB} GB`,
      icon: StorageIcon,
      color: theme.palette.secondary.main,
      progress: {
        percent: storagePercent,
        max: profile?.max_storage_gb || 0,
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        subtitle={
          lang.dashboardSubtitle ||
          "Here's an overview of your account activity."
        }
        title={`${lang.welcomeBack || "Welcome back"}${
          profile?.name ? `, ${profile.name}` : ""
        }! 👋`}
        icon={Face}
      />

      {/* ── Stat Cards Row ── */}
      <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
        {cards.map((card) => (
          <Grid
            key={card.title}
            size={{ xs: 12, sm: 6, lg: 3 }}
            sx={{ display: "flex" }}
          >
            <StatCard {...card} />
          </Grid>
        ))}

        {/* ── Recent Downloads ── */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={0.5}>
                {lang.recentDownloads || "Recent Downloads"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={2.5}
              >
                Your latest torrent activity
              </Typography>
              <Divider sx={{ mb: 2.5 }} />

              {downloads.length === 0 ? (
                <Box textAlign="center" py={5}>
                  <CloudDownloadIcon
                    sx={{
                      fontSize: 52,
                      color: alpha(theme.palette.primary.main, 0.18),
                      mb: 1.5,
                    }}
                  />
                  <Typography color="text.secondary" variant="body2">
                    {lang.noDownloadsYet ||
                      "No downloads yet. Start by adding a magnet link!"}
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {downloads.map((dl) => (
                    <Box
                      key={dl.uid}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: alpha(theme.palette.primary.main, 0.3),
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                        },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={dl.status === "downloading" ? 1 : 0}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ maxWidth: "65%" }}
                        >
                          {dl.torrent_name || "Fetching name..."}
                        </Typography>
                        <Chip
                          label={dl.status}
                          size="small"
                          color={statusColor(dl.status)}
                          sx={{
                            fontWeight: 600,
                            height: 22,
                            fontSize: "0.7rem",
                          }}
                        />
                      </Stack>
                      {dl.status === "downloading" && (
                        <LinearProgress
                          variant="determinate"
                          value={dl.progress || 0}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            "& .MuiLinearProgress-bar": { borderRadius: 2 },
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDashboardHome;
